import { BookmarkContent } from '@/components/BookmarkContent'
import { Popover } from '@/components/ui/Popover'
import { Sheet, SheetDragHandle, SheetScrollView } from '@/components/ui/Sheet'
import { useIsLandscapeMobile, useIsMobile } from '@/lib/useDeviceType'
import React, { useCallback, useMemo, useState } from 'react'
import { YStack } from 'tamagui'

type BookmarkSaveBase = {
  children: React.ReactNode
  onSave: (isBookmarked: boolean) => void
  showError: (msg: string) => void
  showSuccess: (msg: string) => void
  onIconPress?: () => void
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end'
}

export type BookmarkSaveProps =
  | (BookmarkSaveBase & {
      mode: 'lesson'
      lessonId: number
      lessonUrl: string
      courseId?: number
      collectionId?: number
    })
  | (BookmarkSaveBase & {
      mode: 'course'
      courseId: number
      courseUrl: string
    })

export function BookmarkSave(props: BookmarkSaveProps) {
  const { children, onSave, showError, showSuccess, onIconPress, placement = 'bottom-end' } = props
  const contentProps = props.mode === 'lesson'
    ? { mode: 'lesson' as const, lessonId: props.lessonId, lessonUrl: props.lessonUrl, courseId: props.courseId, collectionId: props.collectionId }
    : { mode: 'course' as const, courseId: props.courseId, courseUrl: props.courseUrl }
  const [modalOpen, setModalOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const isMobile = useIsMobile()
  const isLandscapeMobile = useIsLandscapeMobile()
  const snapPoints = useMemo(() => [isLandscapeMobile ? 80 : 60], [isLandscapeMobile])

  const handleSave = useMemo(
    () => (isBookmarked: boolean) => {
      onSave(isBookmarked)
    },
    [onSave]
  )

  const handleClose = useCallback(() => {
    setModalOpen(false)
    setSheetOpen(false)
  }, [])

  const handleMobileOpen = useCallback(() => {
    setSheetOpen(true)
    if (onIconPress) {
      requestAnimationFrame(() => onIconPress())
    }
  }, [onIconPress])

  const bookmarkContent = (sheetMode: boolean) => (
    <BookmarkContent
      onSave={handleSave}
      onClose={handleClose}
      showError={showError}
      showSuccess={showSuccess}
      isOpen
      ScrollViewComponent={sheetMode ? (SheetScrollView as any) : undefined}
      {...contentProps}
    />
  )

  const handleSheetOpenChange = useCallback((open: boolean) => {
    setSheetOpen(open)
    if (open) setModalOpen(false)
  }, [])

  const handlePopoverOpenChange = useCallback((open: boolean) => {
    setModalOpen(open)
    if (open) setSheetOpen(false)
  }, [])

  return (
    <>
      <Popover
        open={!isMobile && modalOpen}
        onOpenChange={handlePopoverOpenChange}
        onMobileOpen={handleMobileOpen}
        placement={placement}
        offset={8}
        width={300}
        maxWidth={300}
        maxHeight={380}
        content={
          <YStack height={380} width="100%">
            {bookmarkContent(false)}
          </YStack>
        }
      >
        {children}
      </Popover>

      <Sheet
        open={isMobile && sheetOpen}
        onOpenChange={handleSheetOpenChange}
        snapPoints={snapPoints}
        backgroundColor="#FFFFFF"
      >
        <YStack f={1} w="100%" minHeight={0} backgroundColor="#FFFFFF">
          <SheetDragHandle />
          {isMobile && sheetOpen ? bookmarkContent(true) : null}
        </YStack>
      </Sheet>
    </>
  )
}
